import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brain, Sparkles, Upload, Save, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function AdminAi() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [audience, setAudience] = useState("");
  const [loading, setLoading] = useState(false);
  const [course, setCourse] = useState(null);

  const generate = async () => {
    if (!file) return toast.error("Selecione um arquivo");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (audience) fd.append("audience", audience);
      const { data } = await api.post("/ai/generate-course", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCourse(data);
      toast.success("Curso gerado pela IA");
    } catch (e) { toast.error("Falha na geração: " + (e.response?.data?.detail || e.message)); }
    setLoading(false);
  };

  const saveCourse = async () => {
    try {
      await api.post("/ai/save-generated-course", course);
      toast.success("Curso salvo como rascunho. Edite em 'Gerenciar cursos' para publicar.");
      setCourse(null); setFile(null);
    } catch { toast.error("Erro ao salvar"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-ai-page">
      <header>
        <div className="eyebrow">Inteligência artificial</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
          <Brain className="w-8 h-8 text-[var(--unigr-red)]" /> Gerar curso a partir de documento
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          Envie PDF, DOCX ou PPTX. Nossa IA (Claude Sonnet 4.5) gera estrutura, módulos, resumo e avaliação automaticamente.
        </p>
      </header>

      <Card><CardContent className="p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Documento (PDF, DOCX, PPTX)</Label>
            <div className="mt-1 flex gap-2 items-center">
              <input ref={inputRef} type="file" accept=".pdf,.docx,.pptx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ai-file-input" />
              <Button variant="outline" onClick={() => inputRef.current?.click()} data-testid="ai-pick-file"><Upload className="w-4 h-4 mr-2" />Selecionar</Button>
              <div className="text-sm text-slate-600 truncate">{file?.name || "Nenhum arquivo selecionado"}</div>
            </div>
          </div>
          <div>
            <Label>Público-alvo (opcional)</Label>
            <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Ex: Operadores de Monitoramento" />
          </div>
        </div>
        <Button onClick={generate} disabled={loading || !file} className="btn-red" data-testid="ai-generate-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando…</> : <><Sparkles className="w-4 h-4 mr-2" />Gerar curso com IA</>}
        </Button>
      </CardContent></Card>

      {course && (
        <Card data-testid="ai-result">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <Badge variant="outline">Gerado por IA</Badge>
                <div className="font-display text-2xl font-extrabold mt-2">{course.title}</div>
                <p className="text-sm text-slate-600 mt-1">{course.description}</p>
              </div>
              <Button onClick={saveCourse} className="btn-navy" data-testid="ai-save-btn"><Save className="w-4 h-4 mr-2" />Salvar como rascunho</Button>
            </div>
            {course.summary && (
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Resumo</div>
                <div className="text-sm text-slate-700 mt-1">{course.summary}</div>
              </div>
            )}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Módulos sugeridos</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(course.modules || []).map((m, i) => (
                  <div key={i} className="border border-[var(--unigr-border)] rounded-md p-3">
                    <div className="font-medium">{i + 1}. {m.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{m.duration_minutes || 10} min</div>
                    <div className="text-sm text-slate-600 mt-1">{m.description}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Avaliação ({course.evaluation?.questions?.length || 0} questões)</div>
              <div className="space-y-2">
                {(course.evaluation?.questions || []).map((q, i) => (
                  <div key={i} className="border border-[var(--unigr-border)] rounded-md p-3 text-sm">
                    <div className="font-medium">{i + 1}. {q.text}</div>
                    <ul className="mt-1 text-slate-600 text-xs space-y-0.5">
                      {q.options.map((o, oi) => <li key={oi}>{q.correct_index === oi ? "✓ " : "○ "}{o}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            {course.complementary_materials?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Materiais complementares sugeridos</div>
                <ul className="text-sm list-disc pl-5">
                  {course.complementary_materials.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
